import { TIoTDevice, TIoTProvider, TIoTWebhook, EIoTProviders, TIoTAlertStatus } from '@treedis/tour-loader';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { VSTourAsset } from '@/_models/virtualStaging/VSObject';
import PublicAPI from '../../api/TreedisPublicDAO';
import { getDeviceMediaImage, iotToast } from './config';
import { verifyLimit, handleLocalStorageAlerts, markAlertAsTriggered } from '../../utils/iot/deviceAlerts';
import { hexToRgb } from '../util';

export interface IotDevicePosition {
  id: number;
  deviceId: number;
  x: number;
  y: number;
  positionX: number;
  positionY: number;
  positionZ: number;
  imageUrl: string;
  iconUrl: string;
  vsImage: {
    url: string;
  };
  [key: string]: any;
}

type Props = {
  showcase: any;
  tourId: number;
  tourSlug: string;
  iot: {
    sceneAssets: VSTourAsset[];
    lockDeviceId?: number;
    moveToDevice?: TIoTDevice;
    devices?: TIoTDevice[];
    newDoorNotification?: TIoTWebhook;
  };
  updateIotState: (state: any) => void;
  setState: (state: any) => void;
  moveToTagIO: (tagSid: string) => void;
  tags: any[]
};

export default function IotLogic({
  showcase: showcaseProp,
  iot,
  tourId,
  tourSlug,
  updateIotState,
  setState,
  moveToTagIO,
  tags,
}: Props) {
  const [devices, setDevices] = useState<TIoTDevice[]>();
  const [providers, setProviders] = useState<TIoTProvider[]>();
  const { scene, showcase } = showcaseProp;
  const { sceneAssets, lockDeviceId, moveToDevice } = iot;
  const initialDataRequest = useRef(false);
  const initialStreamRequest = useRef(false);
  const msSqlProvider = useMemo(
    () => providers?.find((provider) => provider?.provider?.name === EIoTProviders.MS_SQL),
    [providers],
  );
  const rhombusProvider = useMemo(
    () => providers?.find((provider) => provider?.provider?.name === EIoTProviders.RHOMBUS),
    [providers],
  );
  const twinmakerProvider = useMemo(
    () => providers?.find((provider) => provider?.provider?.name === EIoTProviders.TWINMAKER),
    [providers],
  );
  const tanklogixProvider = useMemo(
    () => providers?.find((provider) => provider?.provider?.name === EIoTProviders.TANKLOGIX),
    [providers],
  );
  const thingWorxProvider = useMemo(
    () => providers?.find((provider) => provider?.provider?.name === EIoTProviders.THINGWORX),
    [providers],
  );
  const msSqlDevices = useMemo(
    () => devices?.filter((d) => d.providerId === msSqlProvider?.provider?.id),
    [devices, msSqlProvider],
  );
  const rhombusDevices = useMemo(
    () => devices?.filter((d) => d.providerId === rhombusProvider?.provider?.id),
    [devices, rhombusProvider],
  );
  const twinmakerDevices = useMemo(
    () => devices?.filter((d) => d.providerId === twinmakerProvider?.provider?.id),
    [devices, twinmakerProvider],
  );
  const tanklogixDevices = useMemo(
    () => devices?.filter((d) => d.providerId === tanklogixProvider?.provider?.id),
    [devices, tanklogixProvider],
  );
  const thingWorxDevices = useMemo(
    () => devices?.filter((d) => d.providerId === thingWorxProvider?.provider?.id),
    [devices, thingWorxProvider],
  );

  useEffect(() => {
    (async () => {
      setDevices(iot?.devices);
      const providersRes = await PublicAPI.getIoTProviders({
        query: {
          tourId,
        },
      });

      setProviders(providersRes?.data);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iot]);

  useEffect(() => {
    if (rhombusDevices?.length && !initialStreamRequest.current) {
      (async () => {
        const promises = rhombusDevices.map(async (device) => {
          const stream = await PublicAPI.getStream({
            query: {
              providerId: device.providerId,
              deviceId: device.id,
              tourId: device.tourId,
            },
          });
          if (stream?.data?.sharedLiveVideoStreamUrl) {
            device.videoUrl = stream?.data?.sharedLiveVideoStreamUrl;
          }
          return device;
        });
        initialStreamRequest.current = true;
        const result = await Promise.all(promises);
        const updatedDevices = devices?.map((d) => ({
          ...d,
          videoUrl: result?.find((rd) => rd.id === d.id)?.videoUrl,
        }));

        const favouriteCameras = await PublicAPI.getFavouriteCameras({
          query: {
            tourId,
          },
        });

        updateIotState({
          devices: updatedDevices,
          providers,
          favouriteCameras: favouriteCameras?.data,
        });
      })();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rhombusDevices]);

  useEffect(() => {
    async function fetchAndUpdateDeviceData() {
      if (msSqlDevices?.length) {
        const deviceDataRes = await PublicAPI.getIoTMSSQLDevicesData(tourSlug, {
          tags: msSqlDevices?.map((d) => d.externalDeviceId),
        });
        const updatedDevices = devices?.map((d) => ({
          ...d,
          value: deviceDataRes?.data?.find((dd) => dd.tag === d.externalDeviceId)?.value,
          time: deviceDataRes?.data?.find((dd) => dd.tag === d.externalDeviceId)?.time,
        }));
        updateIotState({ devices: updatedDevices, providers });
      }

      if (twinmakerDevices?.length) {
        const deviceDataRes = await PublicAPI.getIoTTwinmakerDevicesData(tourSlug, {
          tags: twinmakerDevices?.map((d) => d.externalDeviceId), // TODO
        });
        const updatedDevices = devices?.map((d) => ({
          ...d,
          value: deviceDataRes?.data?.find((dd) => dd?.name === d?.externalDeviceId)?.value,
          time: deviceDataRes?.data?.find((dd) => dd?.name === d?.externalDeviceId)?.timestamp,
        }));
        updateIotState({ devices: updatedDevices, providers });
      }
    }

    let intervalId;

    if (msSqlDevices?.length || twinmakerDevices?.length) {
      if (!initialDataRequest.current) {
        fetchAndUpdateDeviceData();
        initialDataRequest.current = true;
      }
      intervalId = setInterval(() => {
        fetchAndUpdateDeviceData();
      }, 300000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msSqlDevices, twinmakerDevices]);

  useEffect(() => {
    async function fetchAndUpdateDeviceData() {
      if (tanklogixDevices?.length) {
        const deviceDataRes = await PublicAPI.getIotTanklogixData({
          query: {
            tourId: tanklogixProvider?.tourId,
            providerId: tanklogixProvider?.providerId,
          },
        });
        if (deviceDataRes?.data?.length > 0) {
          const updatedDevices = devices?.map((d) => ({
            ...d,
            additionalInfo: deviceDataRes?.data?.find(
              (dd) => dd?.TagPath === d?.additionalInfo?.TagPath,
            ),
          }));

          updateIotState({ devices: updatedDevices, providers });
        }
      }
    }

    let intervalId;

    if (tanklogixDevices?.length) {
      if (!initialDataRequest.current) {
        fetchAndUpdateDeviceData();
        initialDataRequest.current = true;
      }
      intervalId = setInterval(() => {
        fetchAndUpdateDeviceData();
      }, 60000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tanklogixDevices]);

  useEffect(() => {
    async function fetchAndUpdateDeviceData() {
      if (thingWorxDevices?.length) {
        const deviceDataRes = await PublicAPI.getIotThingWorxData({
          query: {
            tourId: thingWorxProvider?.tourId,
            providerId: thingWorxProvider?.providerId,
          },
        });
        if (deviceDataRes?.data?.length > 0) {
          const updatedDevices = devices?.map((d) => {
          const updDevice = {
              ...d,
            additionalInfo: deviceDataRes?.data?.find(
              (dd) => dd?.externalDeviceId === d?.externalDeviceId,
            )?.additionalInfo,}
            handleDeviceAlert(updDevice);
            return updDevice;
          });


          updateIotState({ devices: updatedDevices, providers });
        }
      }
    }

    let intervalId;

    if (thingWorxDevices?.length) {
      if (!initialDataRequest.current) {
        fetchAndUpdateDeviceData();
        initialDataRequest.current = true;
      }
      intervalId = setInterval(() => {
        fetchAndUpdateDeviceData();
      }, 120000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thingWorxDevices]);

  const iotRenderedAssets = useRef<VSTourAsset[]>([]);

  const replaceImageSceneNode = useCallback(
    async (asset: VSTourAsset) => {
      // If we get newAsset, it means we get a new scene vsImage to replace
      scene?.sceneImage?.remove(asset);
      await scene?.sceneImage?.create(asset);
    },
    [scene?.sceneImage],
  );

  const updateStateDevices = useCallback(
    (deviceId: number, key: string, value: boolean) => {
      setDevices((prev) => {
        const devicesClone = [...(prev || [])];
        const idx = devicesClone.findIndex(
          (d) =>
            d.id.toString() === deviceId.toString() ||
            d.externalDeviceId.toString() === deviceId.toString(),
        );
        if (idx !== -1) {
          devicesClone[idx][key] = value;
        }
        updateIotState({ devices: devicesClone }); // BaseTour iot devices .. needed for iotMenu
        return devicesClone;
      });
    },
    [updateIotState],
  );

  const retryRefetchWithStatusCheck = useCallback(
    async (
      deviceId: string | number,
      originalStatus: boolean,
      maxRetries: number = 3,
      intervalMs: number = 3000,
    ) => {
      let retryCount = 0;

      const checkStatus = async (): Promise<boolean> => {
        try {
          const result = await PublicAPI.getIoTDevices({query: {tourId}});
          const updatedDevices = result?.data;

          const updatedDevice = updatedDevices?.find((device) => device.id === deviceId);

          if (updatedDevice && updatedDevice.unlocked !== originalStatus) {
            updateStateDevices(deviceId as number, 'unlocked', updatedDevice.unlocked);
            return true;
          }

          return false;
        } catch (error) {
          console.error('Error refetching devices:', error);
          return false;
        }
      };

      const retry = async (): Promise<void> => {
        if (retryCount >= maxRetries) {
          return;
        }

        retryCount++;

        const statusChanged = await checkStatus();

        if (statusChanged) {
          return;
        }

        if (retryCount < maxRetries) {
          setTimeout(retry, intervalMs);
        }
      };

      setTimeout(retry, intervalMs);
    },
    [tourSlug, updateStateDevices]
  );

  const handleLockEvent = useCallback(
    (data: TIoTWebhook, locked: boolean) => {
      iotToast(`${data.doorName} door was ${data.doorAction}ed`, locked ? 'locked' : 'unlocked');

      updateIotState({
        newDoorNotification: {
          ...data,
          text: `${data.doorName} door was ${data.doorAction}ed`,
          seen: false,
        },
      });
      updateStateDevices(data.doorId, 'unlocked', !locked);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateStateDevices, iot?.newDoorNotification],
  );

  const hasLimit = (device: TIoTDevice) => {
    const { additionalInfo } = device;
    if (!additionalInfo) return false;

    const isValidLimit = (value: any) => {
      if (!value) return false;
      if (typeof value === 'string') {
        const normalized = value.toLowerCase().replace(/[^a-z0-9]/g, '');
        return normalized !== 'na' && normalized !== 'notapplicable';
      }
      return true;
    };

    return (
      isValidLimit(additionalInfo.AlarmUpperLimit) ||
      isValidLimit(additionalInfo.AlarmLowerLimit) ||
      isValidLimit(additionalInfo.WarningUpperLimit) ||
      isValidLimit(additionalInfo.WarningLowerLimit)
    );
  }
  const handleLockdownEvent = useCallback(
    (data: TIoTWebhook, locked: boolean) => {
      iotToast(
        `Lockdown was ${locked ? 'activated' : 'cancelled'} for ${data.doorName} door`,
        locked ? 'lockdown' : 'locked',
      );
      updateIotState({
        newDoorNotification: {
          ...data,
          text: `${data.doorName} door was ${data.doorAction}ed`,
          seen: false,
        },
      });
      updateStateDevices(data.doorId, 'lockedDown', locked);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateStateDevices],
  );

  const tagUpdateTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const tagFreshDevices = useRef<Map<string, Map<number, any>>>(new Map());

  const updateTagIconColorBatched = useCallback(async (tagSid: string, triggeringDevice?: any) => {
    if (!showcase) return;

    if (triggeringDevice) {
      if (!tagFreshDevices.current.has(tagSid)) {
        tagFreshDevices.current.set(tagSid, new Map());
      }
      tagFreshDevices.current.get(tagSid)!.set(triggeringDevice.id, triggeringDevice);
    }

    const existingTimeout = tagUpdateTimeouts.current.get(tagSid);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    const timeout = setTimeout(async () => {
      const statusColors = {
        'alarm': '#FF6969',
        'warning': '#FFC300',
        'normal': '#4CAF50',
        'nolimits': '#2196F3', // Blue when all devices have no limits
      };

      const statusPriorities = {
        'alarm': 3,
        'warning': 2,
        'normal': 1,
        'default': 1,
      };

      try {
        const tagsArray = Array.from(tags.values());
        const foundTag: any = tagsArray.find((t: any) => {
          return tagSid && (t.sid === tagSid || t.defaultSid === tagSid);
        });

        if (!foundTag) return;


        const devicesForThisTag = devices?.filter(d =>
          d?.relatedAssets?.some(asset =>
            asset.type === 'tag' && asset.id === tagSid
          )
        ) || [];

        const freshDevicesMap = tagFreshDevices.current.get(tagSid) || new Map();

        let highestPriority = 0;
        let highestStatus: 'alarm' | 'warning' | 'normal' | 'nolimits' | 'default' = 'normal';

        const thingWorxDevicesForTag = devicesForThisTag.filter(device =>
          device.providerId === thingWorxProvider?.provider?.id
        );

        const devicesToEvaluate = thingWorxDevicesForTag.filter(device => {
          const deviceToCheck = freshDevicesMap.get(device.id) || device;
          return hasLimit(deviceToCheck);
        });

        const allThingWorxNoLimits = thingWorxDevicesForTag.length > 0 && devicesToEvaluate.length === 0;

        if (allThingWorxNoLimits) {
          highestStatus = 'nolimits';
        } else {
          devicesToEvaluate.forEach(device => {
            const deviceToCheck = freshDevicesMap.get(device.id) || device;

            if (!deviceToCheck.additionalInfo || !deviceToCheck.additionalInfo.ThingName) {
              return;
            }

            const { status } = verifyLimit(
              deviceToCheck.additionalInfo,
              deviceToCheck.additionalInfo.ThingName
            );

            const priority = statusPriorities[status] || 1;

            if (priority > highestPriority) {
              highestPriority = priority;
              highestStatus = status === 'default' ? 'normal' : status;
            }
          });

          if (highestPriority === 0) {
            highestStatus = 'normal';
          }
        }

        const color = statusColors[highestStatus] || statusColors.normal;
        const rgb = hexToRgb(color, true);



        await showcase.Mattertag.editColor(foundTag.sid, rgb);

      } catch (error) {
        console.error(`Failed batched update for tag ${tagSid}:`, error);
      } finally {
        tagUpdateTimeouts.current.delete(tagSid);
        tagFreshDevices.current.delete(tagSid);
      }
    }, 100);

    tagUpdateTimeouts.current.set(tagSid, timeout);
  }, [showcase, tags, devices, thingWorxProvider, hasLimit]);


  const handleDeviceAlert = useCallback(
    (device: any) => {
      const { additionalInfo, id: deviceId } = device;

      if (!additionalInfo || !additionalInfo.ThingName) {
        return;
      }

      const { status, message } = verifyLimit(additionalInfo, additionalInfo.ThingName);

      const { shouldNotify } = handleLocalStorageAlerts(deviceId, message, status);

      const relatedTagAssets = device?.relatedAssets?.filter((a) => a.type === 'tag') || [];
      relatedTagAssets.forEach(tagAsset => {
        if (tagAsset.id) {
          updateTagIconColorBatched(tagAsset.id, device);
        }
      });

      if (shouldNotify) {
        const iconType = status === 'alarm' ? 'bell' : status === 'warning' ? 'bell' : undefined;
        const toastType = status === 'alarm' ? 'error' : status === 'warning' ? 'warning' : 'info';
        const toastStyle = {
          width: '450px',
          height: '150px',
          ...(status === 'alarm' ? { color: '#FF6969' } :
             status === 'warning' ? { color: '#FFC300' } : {})
        };

        iotToast(message, iconType, toastType, toastStyle);
        markAlertAsTriggered(deviceId);
      }
    },
    [updateTagIconColorBatched],
  );

  const handleMoveToLock = useCallback(
    async (d: TIoTDevice) => {
      updateIotState({
        moveToDevice: null,
      });
      const relatedTag = d.relatedAssets?.find((a) => a.type === 'tag');
      if (relatedTag) {
        moveToTagIO(`${relatedTag.id}`);
      }
      if (!d.toursVsImageId) return;
      const vsObject = sceneAssets.find((o) => o.id === d.toursVsImageId);
      if (!vsObject) return;

      showcase?.Sweep?.moveTo(vsObject.sweepUuid, {
        rotation: {
          x: Number(vsObject.srX),
          y: Number(vsObject.srY),
          z: Number(vsObject.srZ),
        },
        transition: showcase?.Sweep?.Transition.FLY,
      }).catch(function (error) {
        console.log('Failed showcase.Sweep.moveTo', error);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sceneAssets, updateIotState, showcase?.Sweep],
  );

  const handleUnlockAgree = useCallback(
    async (d: TIoTDevice, code?: string) => {
      const promise = !d.unlocked ? PublicAPI.unlockIoTDevice : undefined;

      const res = await promise({
        data: {
          tourId,
          deviceId: d.id,
          providerId: d.providerId,
          ...(code && { passCode: Number(code) }),
        },
      });

      setState({ confirmDialog: { open: false } });
      if (res?.message !== 'Success.') {
        // Not ideal to check for message, but the status code is too generic
        // We have situations where the message is either 'Wrong Pass Code' or 'Invalid request' and in both cases we need to show to the user 'Wrong code!'
        // 4 digits wrong code will generate a 'Wrong Pass Code' message and 3 digits wrong code will generate a 'Invalid request' message
        // But we also have situations where the message is 'The door has no assigned Kisi controller' and in this case we need to show to the user the message from the server
        if (res?.message === 'Wrong Pass Code' || res?.message === 'Invalid request') {
          iotToast('Wrong code!', undefined, 'error');
        } else {
          iotToast(res?.message, undefined, 'error');
        }
        // return;
      }

      updateStateDevices(d.id, 'unlocked', true);

      retryRefetchWithStatusCheck(d.id, !d.unlocked, 3, 3000);
    },
    [setState, tourId, updateStateDevices, retryRefetchWithStatusCheck],
  );

  const handleUnlock = useCallback(
    async (d: TIoTDevice) => {
      updateIotState({
        lockDeviceId: null,
      });

      if (!d.online) {
        iotToast(`${d.name} door is offline`, undefined, 'error');
        return;
      }

      if (d.lockedDown) {
        iotToast(`${d.name} door is on lockdown`, 'lockdown');
        return;
      }

      if (d.unlocked) {
        iotToast(`${d.name} door will automatically lock soon`, undefined, 'info');
        retryRefetchWithStatusCheck(d.id, d.unlocked, 3, 3000);
        return;
      }

      const hasCode = providers?.some((p) => p.hasPassCode);
      const title = 'Confirm action';
      const desc = `Are you sure you want to <br /> ${!d.unlocked ? 'unlock' : 'lock'} ${
        d.name
      } door?`;

      setState({
        confirmDialog: {
          open: true,
          title,
          desc,
          showInput: hasCode,
          onAgree: async (code?: string) => handleUnlockAgree(d, code),
          onCancel: () => {
            setState({ confirmDialog: { open: false } });
          },
        },
      });
    },
    [handleUnlockAgree, providers, setState, updateIotState, retryRefetchWithStatusCheck],
  );

  const connectIotSocket = useCallback(() => {
    const iotSocket = io(process.env.NEXT_PUBLIC_API, {
      transports: ['websocket'],
      autoConnect: true,
    });

    iotSocket.on('lock-device', async (data) => {
      if (data.tourIds?.includes(tourId)) {
        handleLockEvent(data, true);
      }
    });

    iotSocket.on('unlock-device', async (data) => {
      if (data.tourIds?.includes(tourId)) {
        handleLockEvent(data, false);
      }
    });

    iotSocket.on('lockdown-device', async (data) => {
      if (data.tourIds?.includes(tourId)) {
        handleLockdownEvent(data, true);
      }
    });

    iotSocket.on('unlockdown-device', async (data) => {
      if (data.tourIds?.includes(tourId)) {
        handleLockdownEvent(data, false);
      }
    });
  }, [handleLockEvent, handleLockdownEvent, tourId]);

  useEffect(() => {
    if (lockDeviceId) {
      const lockDevice = devices?.find(
        (d) => d.id === lockDeviceId || d.toursVsImageId === lockDeviceId,
      );
      if (lockDevice) handleUnlock(lockDevice);
    }
  }, [lockDeviceId, handleUnlock, devices]);

  useEffect(() => {
    if (moveToDevice) {
      handleMoveToLock(moveToDevice);
    }
  }, [moveToDevice, handleMoveToLock]);

  useEffect(() => {
    connectIotSocket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Updating scene IoT images/tags
  useEffect(() => {
    if (!scene) return;

    devices?.forEach((d) => {
      if (!d.toursVsImageId) return;

      const asset = sceneAssets?.find((a) => a.id === d.toursVsImageId);
      if (!asset) return;

      const mediaImage = getDeviceMediaImage(d);

      // If asset is already rendered, we need to check if we need to replace it with a new one
      const renderedAsset = iotRenderedAssets.current?.find((a) => a.id === asset.id);
      if (mediaImage?.title !== renderedAsset?.details?.title) {
        // @ts-ignore
        const newAsset: VSTourAsset = { ...asset, details: mediaImage };
        replaceImageSceneNode(newAsset);

        const index = iotRenderedAssets.current.findIndex((a) => a.id === asset.id);
        if (index !== -1) {
          iotRenderedAssets.current[index] = newAsset as VSTourAsset;
        } else {
          iotRenderedAssets.current.push(newAsset);
        }
      }
    });
  }, [devices, scene, sceneAssets, replaceImageSceneNode]);

  return <></>;
}












